/* ─── Design Tokens ─── */

export const colors = {
  coral: '#EE8172',
  coralDark: '#D4685A',
  /* RW-2960: the coral for TEXT. #EE8172 (2.4:1 on the #F6F5F1 cream) and
     #D4685A (3.25:1) both miss WCAG AA's 4.5:1 for normal-size text, so
     anything that is read — links, text buttons, labels, counts — uses this.
     Fills, borders and button backgrounds keep `coral`; `coralDark` stays the
     hover/pressed fill. #A63F33 rather than a lighter #B4483B: that one falls
     to 4.0:1 on the `pearl` chip tint coral text also sits on. Ratios are
     pinned by src/a11yGuards.test.js. */
  coralInk: '#A63F33',
  coralLight: '#F5A898',
  noir: '#0F1011',
  pearl: '#F6D9D4',
  pearlLight: '#FDF0EE',
  purple: '#5C50A1',
  purpleLight: '#D1DAEF',
  yellow: '#EFDD84',
  cream: '#FFF7E1',
  blue: '#728AC5',
  gray1: '#9C9FA4',
  gray2: '#81858C',
  gray3: '#4A4F55',
  white: '#FFFFFF',
  errorRed: '#DB6A5E',

  /* ─── Coming Soon reskin (CS design system, Reve OS) — additive warm/match
     tokens. New keys only; existing values above are untouched. ─── */
  canvas: '#F0EEE9',      // warm page background
  line: '#E8E2DA',        // warm hairline
  line2: '#F3EEE6',       // faint warm hairline
  panel: '#FAF7F2',
  panelDeep: '#F5EFE5',
  text: '#2A2622',
  textSoft: '#4A4F55',
  muted: '#6B7280',
  mute2: '#9CA3AF',
  coralSoft: '#FDF0EE',   // == pearlLight, named for CS parity
  moss: '#3B5D44',        // "can show now"
  mossSoft: '#E8F0E9',
  mossMid: '#7FA587',
  gold: '#C89B3C',        // "good" match
  goldSoft: '#FAF0D4',
  indigo: '#4F46E5',
  indigoSoft: '#EEF0FF',
  indigoDeep: '#3730A3',

  /* ─── Recognition module (RW-971) — GOLD/trophy identity tokens, ported
     verbatim from the design's rec-shared.jsx RG token system. New keys only;
     existing values above are untouched. These power the Recognition module
     surfaces (Home, Recognize, etc.). ─── */
  goldRec: '#B5852A',     // the Recognition signature accent (rec-shared `gold`)
  goldDeep: '#8A6418',
  goldDark: '#6F4E12',
  goldMid: '#D7B25A',
  goldRecSoft: '#FAF1D8', // rec-shared `goldSoft` (theme.js already has a `goldSoft`)
  goldGlow: '#FBF6E7',
  goldLine: '#EAD9A8',
  recBg: '#FAF8F3',       // warm near-white Recognition canvas

  /* ─── Login / auth surface (RW-2564) ─── */
  /* The split-screen sign-in draws hairlines on white, where the warm `line`
     family reads as a tint rather than a rule. These are the noir at two
     opacities, the way the design canvas specifies them. */
  authLineStrong: 'rgba(15,16,17,0.18)',
  authLineFaint: 'rgba(15,16,17,0.06)',
  /* And its own error pair, which IS a deliberate second one — `tone.err`
     (RW-2141) exists so error colour does not proliferate, and everywhere
     inside the app should still use it. This surface is the exception Clint
     called: `tone.err.soft` (#FEE2E2) is a cool pink that reads as imported
     next to the coral and pearl of the sign-in frame, and the front door is
     the one screen where that shows. These are the canvas's values.
     Do not reach for them anywhere but the auth screens. */
  authErr: '#B3261E',
  authErrSoft: '#FBEDEC',
};

/* ─── Semantic tone tokens (RW-2141) ───────────────────────────────────
   The Producer Home artboard's five tones. These hexes were already loose in
   the codebase — #10B981 / #F59E0B / #EF4444 appear as literals on the
   dashboard alone — so the reskin names them once instead of adding a sixth
   copy. `soft` is the chip/tile ground, `text` the readable foreground on it
   (both AA against their own ground); the bare value is for strokes and dots. */
export const tone = {
  ok:    { base: '#10B981', soft: '#D1FAE5', text: '#065F46' },
  warn:  { base: '#F59E0B', soft: '#FEF3C7', text: '#92400E' },
  err:   { base: '#EF4444', soft: '#FEE2E2', text: '#991B1B' },
  info:  { base: '#3B82F6', soft: '#DBEAFE', text: '#1E40AF' },
  brand: { base: colors.coral, soft: colors.coralSoft, text: colors.coralInk },
};

export const fonts = {
  heading: "'Jost', sans-serif",
  body: "'Jost', sans-serif",
  // CS editorial serif — used for prices & section headings in the listings reskin.
  serif: "'Fraunces', Georgia, serif",
  // RW-2564: the single editorial line on the sign-in photography. Loaded in
  // public/index.html alongside Jost and Fraunces.
  display: "'Playfair Display', Georgia, serif",
};

/* Shared Detail-Page header card styles — used by ListingDetail & TransactionDetail.
   Change a value here to update both pages at once. */
export const detailHeader = {
  card: { background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 20px', marginBottom: 16 },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 8 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#111827', fontFamily: "'Jost', sans-serif" },
  subtitle: { margin: '2px 0 0', fontSize: 13, color: '#6B7280' },
  actionsRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  secondaryBtn: { padding: '8px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 40 },
  statusSelect: { padding: '8px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 40 },
  backBtn: { background: 'none', border: 'none', color: '#6B7280', fontSize: 13, cursor: 'pointer', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 },
};

export const radius = {
  xs: '2px',   // brand-guide button radius (RW-2564 sign in)
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
};

/* ─── Legacy aliases (consumed by existing components) ─── */
export const theme = {
  noir: colors.noir,
  pearl: colors.pearl,
  coral: colors.coral,
  coralInk: colors.coralInk, // RW-2987: AA text on light surfaces; `coral` stays for fills/borders
  purple: colors.purple,
  white: colors.white,
  heading: colors.noir,
  body: colors.gray3,
  subtext: colors.gray1,
};

export const font = fonts.body;

/* ─── Constants ─── */

export const OFFICES = [
  { id: 'all', label: 'All Offices' },
  { id: 'new-orleans', label: 'New Orleans' },
  { id: 'baton-rouge', label: 'Baton Rouge' },
  { id: 'covington', label: 'Covington' },
];

/**
 * @deprecated since RW-11. Use Firebase custom claims via `useUserRole` or
 * the augmented `user.role` field instead. This array is retained ONLY as the
 * permanent emergency fallback for clint@reverealtors.com so a corrupted
 * claim can never lock him out. Do not add new emails here.
 */
export const ADMIN_EMAILS = ['clint@reverealtors.com'];

export const PRIORITIES = [
  { id: 'standard', label: 'Standard' },
  { id: 'important', label: 'Important' },
  { id: 'critical', label: 'Critical' },
];

export const DOC_FOLDERS = [
  'Company Policies',
  'Marketing Templates',
  'Forms & Contracts',
  'Training Resources',
];

export const BADGE_CATEGORIES = [
  'Closed Deal',
  'Above & Beyond',
  'Team Player',
  'New Agent Milestone',
  'Referral Champion',
  'Culture Champion',
  'Training Complete',
];

/**
 * RW-1039: curated feed topics — the shared, canonical tag set surfaced as composer
 * chips (replacing free-form hashtags). `id` is the lowercase token used for navigation
 * (/feed?hashtag=<id>) and trending, matching the existing hashtag system, so topics
 * stay navigable for free. `action` topics DO something server-side when applied:
 * 'tc_ping' notifies the TC queue; 'recognition' cross-posts to the Recognition surface.
 *
 * RW-68: `icon` is a feedIcons.js glyph NAME (plain string — theme.js must never
 * import components, per CLAUDE.md gotcha #11). Consumers resolve it through a
 * bridge (TopicIcon in ComposerModal.js, the RPIcon pattern), not as a text node.
 */
export const FEED_TOPICS = [
  { id: 'justlisted',    label: 'JustListed',    icon: 'home',      color: '#EE8172', action: null },
  { id: 'undercontract', label: 'UnderContract', icon: 'file-text', color: '#4F46E5', action: null },
  { id: 'win',           label: 'Win',           icon: 'award',     color: '#E0A12E', action: 'recognition', actionLabel: 'Drops into the Recognition rail' },
  { id: 'needhelp',      label: 'NeedHelp',      icon: 'help',      color: '#5C50A1', action: 'tc_ping',     actionLabel: 'Pings the TC team' },
  { id: 'comingsoon',    label: 'ComingSoon',    icon: 'key',       color: '#D4685A', action: null },
  { id: 'marketintel',   label: 'MarketIntel',   icon: 'trending',  color: '#7B61C9', action: null },
  { id: 'referral',      label: 'Referral',      icon: 'compass',   color: '#5C8A5A', action: null },
  { id: 'flow',          label: 'FLOW',          icon: 'zap',       color: '#6E59C7', action: null },
  { id: 'inspection',    label: 'Inspection',    icon: 'search',    color: '#728AC5', action: null },
  { id: 'closing',       label: 'Closing',       icon: 'handshake', color: '#4F8A6B', action: null },
];
// RW-1563: max length of a REQUESTED topic name. Mirrors MAX_LABEL in
// functions/src/domain/topicRequests.js — the callable is the authority and
// re-validates; this is only so the form can warn before a round trip.
export const MAX_TOPIC_LABEL = 24;

export const topicById = (id) => FEED_TOPICS.find((t) => t.id === id) || null;

export const DEFAULT_CHECKLIST = [
  'Complete profile',
  'Upload headshot',
  'Join office Google Chat space',
  'Attend first training session',
  'Complete first showing',
  'Close first transaction',
  'Earn first badge',
];

/* ─── Utility Functions ─── */

export function formatPostTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) { const n = Math.floor(diff / 60000); return `${n} minute${n === 1 ? '' : 's'} ago`; }
  if (diff < 86400000) { const n = Math.floor(diff / 3600000); return `${n} hour${n === 1 ? '' : 's'} ago`; }
  if (diff < 604800000) { const n = Math.floor(diff / 86400000); return `${n} day${n === 1 ? '' : 's'} ago`; }
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function officeLabel(id) {
  const o = OFFICES.find((x) => x.id === id);
  return o ? o.label : formatOfficeName(id);
}

export function formatOfficeName(office) {
  if (!office) return '';
  const map = {
    'new-orleans': 'New Orleans',
    'baton-rouge': 'Baton Rouge',
    'covington': 'Covington',
    'all': 'All Offices',
  };
  return map[office.toLowerCase()] || office;
}

export function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/**
 * Format a number as US currency: "$1,500,000"
 * Handles null/undefined/strings. Always saves as plain number — this is display only.
 */
const currencyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

export function formatCurrency(value) {
  if (value == null || value === '') return '';
  const n = typeof value === 'string' ? parseInt(value.replace(/\D/g, ''), 10) : Number(value);
  if (!n && n !== 0) return '';
  return currencyFmt.format(n);
}

/**
 * RW-2145: compact currency for the 28px stat tiles — "$1.2M", "$450K", "$900".
 * House rule, so the two money styles stop sitting side by side on the
 * dashboard: large tile numbers use this; detail rows, breakdowns and
 * progress-bar ends (≤13px) use formatCurrency's full figure.
 */
export function formatCompactCurrency(value) {
  const n = typeof value === 'string' ? parseInt(value.replace(/\D/g, ''), 10) : Number(value);
  if (!n || n < 0) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

/** Format a number with comma separators (no $ sign) — for sqft, counts, etc. */
export function fmtNum(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? '' : n.toLocaleString('en-US');
}

/** Format input value with commas as user types (no $ sign, digits only) */
export function formatNumberInput(value) {
  if (value == null || value === '') return '';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('en-US');
}

/** Parse a formatted string back to a plain number (for Firestore saves) */
export function parseNumber(value) {
  if (value == null || value === '') return null;
  const n = parseInt(String(value).replace(/\D/g, ''), 10);
  return Number.isNaN(n) ? null : n;
}

// Roles surfaced in the User Management dropdown. Includes the legacy
// labels (leadership/manager/staff) for backwards compatibility AND the
// canonical claim-backed roles enforced by setUserRole / Firestore rules
// (super_admin / admin / agent_services / trainer / tc / agent — see
// useUserRole.js). RW-564 added agent_services.
export const ROLES = ['super_admin', 'admin', 'agent_services', 'trainer', 'tc', 'leadership', 'manager', 'agent', 'staff'];

export function isLeadership(userDoc) {
  if (!userDoc) return false;
  const role = userDoc.role || userDoc.customRole;
  return role === 'leadership';
}

export function isLeadershipEmail(email, users) {
  if (!email || !users) return false;
  const u = users.find((x) => x.email === email);
  return u ? isLeadership(u) : false;
}
